import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedProjects = () => {
  const [hoveredProject, setHoveredProject] = useState(null);

  const projects = [
    {
      id: 1,
      title: "E-Commerce Analytics Dashboard",
      category: "Full-Stack Application",
      description: "Real-time analytics platform for e-commerce businesses with advanced data visualization and predictive insights.",
      problem: "Merchants needed unified view of sales, inventory, and customer behavior across multiple channels.",
      solution: "Built comprehensive dashboard with React, Node.js, and MongoDB featuring real-time updates and ML-powered forecasting.",
      impact: "Increased merchant decision-making speed by 65% and reduced inventory waste by 30%.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      techStack: ["React", "Node.js", "MongoDB", "D3.js", "Socket.io"],
      liveDemo: "https://demo-analytics.example.com",
      github: "https://github.com/username/analytics-dashboard",
      featured: true
    },
    {
      id: 2,
      title: "AI-Powered Code Review Tool",
      category: "Developer Tools",
      description: "Intelligent code review assistant that provides automated feedback and suggestions for code quality improvements.",
      problem: "Development teams struggled with consistent code review standards and time-consuming manual reviews.",
      solution: "Created AI-powered tool using Python, TensorFlow, and React that analyzes code patterns and provides intelligent suggestions.",
      impact: "Reduced code review time by 40% and improved code quality scores by 55% across development teams.",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop",
      techStack: ["Python", "TensorFlow", "React", "FastAPI", "PostgreSQL"],
      liveDemo: "https://demo-codereview.example.com",
      github: "https://github.com/username/ai-code-review",
      featured: true
    },
    {
      id: 3,
      title: "Real-Time Collaboration Platform",
      category: "Web Application",
      description: "Multi-user collaboration platform with real-time editing, video calls, and project management features.",
      problem: "Remote teams needed seamless collaboration tools that combined document editing, communication, and project tracking.",
      solution: "Developed platform using React, WebRTC, and WebSocket technology with real-time synchronization and conflict resolution.",
      impact: "Improved team productivity by 45% and reduced project completion time by 25% for distributed teams.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop",
      techStack: ["React", "WebRTC", "Socket.io", "Express", "Redis"],
      liveDemo: "https://demo-collab.example.com",
      github: "https://github.com/username/collab-platform",
      featured: true
    },
    {
      id: 4,
      title: "Mobile Fitness Tracking App",
      category: "Mobile Application",
      description: "Cross-platform fitness app with workout tracking, nutrition monitoring, and social features for fitness enthusiasts.",
      problem: "Fitness enthusiasts needed comprehensive tracking solution that worked across devices with social motivation features.",
      solution: "Built React Native app with Redux state management, integrated wearable devices, and gamification elements.",
      impact: "Achieved 4.8/5 app store rating with 100K+ downloads and 85% user retention rate after 3 months.",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop",
      techStack: ["React Native", "Redux", "Firebase", "Node.js", "MongoDB"],
      liveDemo: "https://demo-fitness.example.com",
      github: "https://github.com/username/fitness-app",
      featured: false
    }
  ];

  const featuredProjects = projects?.filter(project => project?.featured);

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="FolderOpen" size={20} className="text-accent" />
            <span className="text-sm font-medium text-text-secondary tracking-wide uppercase">
              Featured Work
            </span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
            Projects That Make Impact
          </h2>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            A showcase of recent projects demonstrating problem-solving skills, 
            technical expertise, and measurable business outcomes.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {featuredProjects?.map((project, index) => (
            <div
              key={project?.id}
              className="group relative bg-card rounded-xl shadow-soft hover:shadow-large transition-all duration-500 overflow-hidden animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredProject(project?.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={project?.image}
                  alt={project?.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent transition-opacity duration-300 ${
                  hoveredProject === project?.id ? 'opacity-100' : 'opacity-0'
                }`}>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex space-x-2 mb-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        iconName="ExternalLink"
                        iconPosition="left"
                        className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30"
                      >
                        Live Demo
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Github"
                        className="text-white hover:bg-white/20"
                      />
                    </div>
                  </div>
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                    {project?.category}
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-accent transition-colors duration-300">
                  {project?.title}
                </h3>
                
                <p className="text-text-secondary text-sm mb-4 line-clamp-2">
                  {project?.description}
                </p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project?.techStack?.slice(0, 3)?.map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md"
                    >
                      {tech}
                    </span>
                  ))}
                  {project?.techStack?.length > 3 && (
                    <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md">
                      +{project?.techStack?.length - 3} more
                    </span>
                  )}
                </div>

                {/* Impact Metric */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="TrendingUp" size={16} className="text-success" />
                    <span className="text-sm font-medium text-success">
                      {project?.impact?.split(' ')?.slice(0, 4)?.join(' ')}
                    </span>
                  </div>
                  
                  <Icon 
                    name="ArrowRight" 
                    size={16} 
                    className="text-text-secondary group-hover:text-accent group-hover:translate-x-1 transition-all duration-300" 
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Projects CTA */}
        <div className="text-center animate-fade-in-up" style={{ animationDelay: '400ms' }}>
          <Button
            variant="outline"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
          >
            View All Projects
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;